import {
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
  IconButton,
  CircularProgress,
  Link,
  Chip,
  Radio,
  Tooltip,
  Box,
  createFilterOptions,
  Checkbox,
  TextField,
} from "@mui/material";
import {
  CustomTabPanel,
  CustomTable,
  CustomeDialog,
  DeleteModal,
  Loader,
  PageListLayout,
  ServerPaginatedtable,
  InputControl,
  LimitedTag,
} from "../../../shared";
import { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useStores } from "../hooks";
import { createColumnHelper } from "@tanstack/react-table";
import { SubRowCustomTable } from "../components";
import { useScheduledSyncList } from "../hooks";
import ImportIcon from "../../../assets/images/Icons/ic_import.svg";
import SaveIcon from "../../../assets/images/Icons/ic_save.svg";
import { Clear } from "@mui/icons-material";
import editIcon from "../../../assets/images/Icons/ic_edit.svg";
import { addDays } from "date-fns";

export const ScheduledSyncList = () => {
  let { id } = useParams();

  let navigate = useNavigate();

  const {
    state,
    listQuery,
    pagination,
    setPagination,
    sorting,
    setSorting,
    search,
    onSearchHandler,
    onDateChangeHandler,
    selectStatus,
    onChangeStatusHandler,
    disableclearButton,
    onClearFilter,
    moduleName,
    onSaveChanges,
    setEditBtnhandler,
    onDateChange,
    isUpdating,
    formValidator,
    categoryValuesHandler,
    CategoryOptions,
    SubCategoryOptions,
    tableData,
    subCategoryValuesHandler,
    onDelete,
    deleteInfo,
    resetDeleteInfo,
    confirmDelete,
  } = useScheduledSyncList(id);

  const columnHelper = createColumnHelper();

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option.name,
  });

  const columns = [
    columnHelper.accessor("scheduled_date", {
      header: () => "Scheduled Date",
      cell: ({ row, getValue }) => {
        const date = new Date(getValue());

        if (row.original.id === state.editId) {
          const dateHelperText = formValidator.current.message(
            "date",
            state.selectedDataDate,
            "required|notPastDate",
          );
          return (
            <InputControl
              type="datePicker"
              size="small"
              label=""
              name="date"
              helperText={dateHelperText}
              error={!!dateHelperText}
              customDatePickerIcon
              datePickerProps={{
                disableMaskedInput: true,
                inputFormat: "dd MMM yyyy",
                minDate: addDays(new Date(), 1),
                value: state.selectedDataDate,
                onChange: (event) =>
                  onDateChange({ target: { name: "date", value: event } }),
              }}
              sx={{
                width: "160px",
              }}
            />
          );
        } else return row.original.scheduled_date;
      },
    }),

    columnHelper.accessor("submitted_date", {
      header: () => "Submitted Date",
    }),

    columnHelper.accessor("user", {
      header: () => "User",
    }),

    columnHelper.accessor("plu", {
      header: () => "PLU Code",
      cell: ({ getValue }) => getValue() || "-",
    }),

    columnHelper.accessor("product_name", {
      header: () => "Finished Product Name",
      cell: ({ getValue, row: { original } }) => (
        <Typography
          onClick={() =>
            navigate(`/recipe/details/${original?.recipe_id}?basic-details`)
          }
          variant="subtitle2"
          sx={{
            textDecoration: "underline",
            cursor: "pointer",
            color: "neutral.10",
          }}
        >
          {getValue()}
        </Typography>
      ),
    }),

    columnHelper.accessor("category", {
      header: () => "Category",
    }),

    columnHelper.accessor("sub_category", {
      header: () => "Sub-Category",
    }),

    columnHelper.accessor("recipe_status", {
      header: () => "Status",
      cell: ({ getValue }) => {
        const success = getValue() === 1;
        return (
          <Chip
            size="small"
            color={success ? "primary" : "gray"}
            variant="filled"
            label={success ? "ACTIVE" : "INACTIVE"}
          />
        );
      },
    }),

    columnHelper.accessor("cost_exc_gst", {
      header: () => "Cost (Ex GST)",
      cell: ({ getValue }) => `$${getValue().toFixed(3)}`,
    }),

    columnHelper.display({
      id: "actions",
      header: () => "",
      cell: ({ row }) => {
        return (
          <>
            {row.original.id === state.editId ? (
              <Stack flexDirection="row">
                <Tooltip title="Save" arrow disableInteractive>
                  <IconButton
                    size="small"
                    onClick={(event) => onSaveChanges(event, row.original)}
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <CircularProgress size={20} color="primary" />
                    ) : (
                      <img src={SaveIcon} alt="save-icon" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" arrow disableInteractive>
                  <IconButton
                    size="small"
                    onClick={() => setEditBtnhandler(null)}
                  >
                    <Clear color="error" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <Stack flexDirection="row" justifyContent="flex-end" gap="8px">
                <IconButton
                  size="small"
                  onClick={() => setEditBtnhandler(row.original)}
                >
                  <Box component="img" src={editIcon} height={20} width={20} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    onDelete(row.original.id);
                  }}
                  sx={{ p: 0 }}
                >
                  <img
                    src="../icons/ic_trash.svg"
                    alt="delete icon"
                    style={{}}
                  />
                </IconButton>
              </Stack>
            )}
          </>
        );
      },
    }),
  ];

  return (
    <>
      <ServerPaginatedtable
        defaultData={listQuery?.data?.syncData || []}
        columns={columns}
        isLoading={listQuery.isLoading}
        pagination={pagination}
        setPagination={(callback) =>
          setPagination({ tableName: moduleName, callback })
        }
        sorting={sorting}
        setSorting={(callback) =>
          setSorting({ tableName: moduleName, callback })
        }
        onSearchChange={onSearchHandler}
        search={search}
        paginationProps={
          listQuery.data?.pagination || { total: 0, lastPage: 1 }
        }
        headerText={
          <Typography variant="h5" fontWeight={500} fontSize="18px">
            Scheduled Sync
          </Typography>
        }
        tableHeaderContent={
          <Stack
            sx={{
              flexDirection: "row",
              gap: "6px",
              width: "100%",
            }}
          >
            <Stack
              sx={{
                flexDirection: "row",
                gap: "6px",
                width: "100%",
              }}
            >
              <InputControl
                type="datePicker"
                size="small"
                label="Date"
                customDatePickerIcon
                datePickerProps={{
                  disableMaskedInput: true,
                  inputFormat: "dd, MMM yyyy",
                  value: tableData?.scheduledSync?.data?.selectedDate || null,
                  onChange: (event) => onDateChangeHandler(event),
                }}
              />
              <InputControl
                type="dropdown"
                variant="filter"
                size="small"
                fullWidth
                multiple={true}
                options={CategoryOptions ?? []}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableCloseOnSelect
                sx={{
                  width: "250px",
                }}
                renderTags={(value) => (
                  <LimitedTag value={value} autocompleteWidth="250px" />
                )}
                value={tableData?.scheduledSync?.data?.categoryfilter ?? []}
                onChange={(event, option) =>
                  categoryValuesHandler(event, option)
                }
                renderOption={(props, option, { selected }) => (
                  <>
                    <li {...props} key={option.id}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option?.name}
                    </li>
                  </>
                )}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Category" />
                )}
              />
              <InputControl
                type="dropdown"
                variant="filter"
                size="small"
                fullWidth
                multiple={true}
                options={SubCategoryOptions ?? []}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableCloseOnSelect
                sx={{
                  width: "250px",
                }}
                renderTags={(value) => (
                  <LimitedTag value={value} autocompleteWidth="250px" />
                )}
                value={tableData?.scheduledSync?.data?.subcategoryfilter ?? []}
                onChange={(event, option) =>
                  subCategoryValuesHandler(event, option)
                }
                renderOption={(props, option, { selected }) => (
                  <>
                    <li {...props} key={option.id}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option?.name}
                    </li>
                  </>
                )}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Sub-Category" />
                )}
              />
              <InputControl
                type="dropdown"
                variant="filter"
                size="small"
                options={selectStatus.statusOptions}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{
                  width: "180px",
                }}
                value={
                  selectStatus?.statusOptions?.find(
                    (option) =>
                      option.id ===
                      tableData?.scheduledSync?.data.selectedStatus,
                  ) || null
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Status" />
                )}
                onChange={(event, option) =>
                  selectStatus.statusValueChangeHandler(option)
                }
              />
              <Button
                type="button"
                size="small"
                variant="text"
                sx={{
                  p: "6px 12px 6px 12px",
                  gap: "6px",
                }}
                disabled={disableclearButton}
                onClick={onClearFilter}
              >
                Clear All
              </Button>
            </Stack>
          </Stack>
        }
      />
      <DeleteModal
        open={deleteInfo?.show}
        handleClose={() => resetDeleteInfo()}
        onConfirm={() => confirmDelete()}
      />
    </>
  );
};
